import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import Page from '../../components/Page';
import Breadcrumb from '../../components/Breadcrumb';
import Pagination from '../../components/Pagination';
import client from '../../lib/feathers';

const LIMIT = 25;

/**
 * The visits log: one row per visit, openable into what actually happened.
 *
 * Aggregate reporting tells you page views went up. This tells you somebody
 * arrived from Perplexity on a Mac, read two winery pages and left without
 * signing in. It is also the only practical way to see whether session
 * tracking is working, which is why it was built before any dashboard.
 */

const CHANNEL_STYLES = {
  ai_referrer: 'bg-purple-100 text-purple-800',
  organic: 'bg-green-100 text-green-800',
  paid: 'bg-amber-100 text-amber-800',
  email: 'bg-blue-100 text-blue-800',
  social: 'bg-pink-100 text-pink-800',
  qr: 'bg-indigo-100 text-indigo-800',
  share: 'bg-teal-100 text-teal-800',
  referral: 'bg-gray-100 text-gray-800',
  internal: 'bg-gray-100 text-gray-600',
  campaign: 'bg-blue-100 text-blue-800',
  direct: 'bg-gray-100 text-gray-500',
};

function Chip({ children, className }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className || 'bg-gray-100 text-gray-800'}`}>
      {children}
    </span>
  );
}

/**
 * The shape of a visit at a glance, without opening it.
 *
 * A dot is a page view, a ring is an action on a listing. Reading the strip
 * tells you "landed, looked, left" versus "landed, looked, called them" faster
 * than any number in the row can.
 */
function ActionStrip({ items }) {
  if (!items || !items.length) return <span className="text-gray-300">-</span>;
  return (
    <span className="inline-flex items-center gap-1" title={items.map((i) => i.label).join(' -> ')}>
      {items.map((i, n) => (
        <span
          key={`${i.at}-${n}`}
          title={i.label}
          className={`inline-block rounded-full ${
            i.kind === 'event'
              ? 'h-1.5 w-1.5 bg-slate-400'
              : 'h-2 w-2 border-2 border-[var(--wine)]'
          }`}
        />
      ))}
    </span>
  );
}

function duration(sec) {
  if (sec === null || sec === undefined) return '-';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  return m < 60 ? `${m}m ${sec % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * The sequence of what happened.
 *
 * Shows the STORY by default: what the person did, and where they went when
 * they left. Impressions (`load`, `nearby`, `search`, `discover`, `list`) are
 * folded away behind a count. They are real and the winery reports count
 * them, but they are a record of a listing being SHOWN rather than of anyone
 * deciding anything, and interleaving them meant one journey read as eighteen
 * indistinguishable rows.
 *
 * Nothing is discarded. "Show N impressions" puts them all back, in place.
 *
 * The API classifies; this only renders. See api/src/lib/action-taxonomy.
 */
function Timeline({ visitId }) {
  const [rows, setRows] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let live = true;
    client.service('admin/visit-log').get(visitId)
      .then((v) => { if (live) setRows(v.timeline || []); })
      .catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [visitId]);

  if (rows === null) return <div className="px-6 py-3 text-sm text-gray-500">Loading...</div>;
  if (!rows.length) {
    return (
      // A visit that recorded nothing is itself a finding, usually that
      // something is not firing, so say so rather than showing an empty box.
      <div className="px-6 py-3 text-sm text-gray-500 italic">
        No events or actions recorded in this visit.
      </div>
    );
  }

  const impressions = rows.filter((t) => t.significance === 'impression').length;
  const shown = showAll ? rows : rows.filter((t) => t.significance !== 'impression');

  /**
   * Where the visitor stopped being anonymous.
   *
   * The SESSION gets a userId when a login claims it, which back-fills the
   * whole visit and makes every row look identified. The rows themselves
   * keep the truth: userId is null on anything collected before signing in.
   * So the boundary is the first row that has one, and marking it is what
   * keeps the journey readable instead of a visit that was always "you".
   */
  const identifiedFrom = shown.findIndex((t) => t.userId);

  return (
    <div className="px-6 py-3">
      <ol className="space-y-1">
        {shown.map((t, i) => {
          const outbound = t.significance === 'outbound';
          const muted = t.significance === 'impression';
          return (
            <Fragment key={t.id}>
            {i === identifiedFrom && (
              // Drawn once, at the crossing. Everything above it happened
              // before we knew who this was.
              <li className="flex items-center gap-2 pt-2 text-xs text-gray-400">
                <span className="h-px flex-1 bg-gray-200" />
                <span className="uppercase tracking-wide">signed in</span>
                <span className="h-px flex-1 bg-gray-200" />
              </li>
            )}
            <li
              className={`flex items-baseline gap-3 text-sm ${muted ? 'opacity-50' : ''}`}
            >
              <span className="w-16 shrink-0 font-mono text-xs text-gray-400">
                {format(new Date(t.createdAt), 'HH:mm:ss')}
              </span>
              <Chip
                className={
                  outbound
                    ? 'bg-emerald-100 text-emerald-800'
                    : (t.kind === 'event' ? 'bg-slate-100 text-slate-700' : 'bg-[var(--wine)]/10 text-[var(--wine)]')
                }
              >
                {/* Leaving for the winery is the event the whole system exists
                    to count, so it never looks like the rows around it. */}
                {outbound ? 'left for' : t.kind}
              </Chip>
              {/* Per row, because a visit that walks from the marketing site
                  into the app is ONE session carrying one surface. Without
                  this a listing read on the web and the same listing opened
                  in the app are the same line twice. */}
              {t.surface && (
                <span className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-gray-400">
                  {t.surface}
                </span>
              )}
              <span className={outbound ? 'font-medium text-gray-900' : 'text-gray-900'}>
                {t.description || t.label}
              </span>
              {/* Who, then what it was called, then where. The winery's name
                  comes from the recorded listing rather than from the page
                  title, so it is right even when the title is a placeholder
                  the screen was still showing while its data loaded. */}
              {t.listingName && <span className="font-medium text-gray-700">{t.listingName}</span>}
              {t.title && t.path && <span className="text-gray-600">{t.title}</span>}
              {t.path && (
                <span className="font-mono text-xs text-gray-500">
                  {t.path}
                  {/* The query says WHICH view of this path, so it belongs
                      beside the path rather than lost in the props blob. */}
                  {t.props && t.props.query
                    ? `?${Object.entries(t.props.query).map(([k, v]) => `${k}=${v}`).join('&')}`
                    : ''}
                </span>
              )}
              {t.durationMs ? <span className="text-gray-600">{Math.round(t.durationMs / 1000)}s on page</span> : null}
              {t.props && Object.keys(t.props).length ? (
                <span className="text-xs text-gray-600 font-mono">{JSON.stringify(t.props)}</span>
              ) : null}
            </li>
            </Fragment>
          );
        })}
      </ol>
      {impressions > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 text-xs text-gray-500 underline hover:text-gray-800"
        >
          {showAll
            ? `Hide ${impressions} impression${impressions === 1 ? '' : 's'}`
            : `Show ${impressions} impression${impressions === 1 ? '' : 's'} the winery counts`}
        </button>
      )}
    </div>
  );
}

export default function Visits() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query] = useSearchParams();

  const page = Number(query.get('page') || 0);
  const channel = query.get('channel') || '';
  const surface = query.get('surface') || '';
  const agent = query.get('agent') || '';
  const identified = query.get('identified') || '';

  const [visits, setVisits] = useState(null);
  const [total, setTotal] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setVisits(null);
    try {
      const res = await client.service('admin/visit-log').find({
        query: {
          $limit: LIMIT,
          $skip: page * LIMIT,
          ...(channel ? { channel } : {}),
          ...(surface ? { surface } : {}),
          ...(agent ? { agent } : {}),
          ...(identified ? { identified } : {}),
        },
      });
      setVisits(res.data);
      setTotal(res.total);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load visits');
    }
  }, [page, channel, surface, identified, agent]);

  useEffect(() => { load(); }, [load]);

  const go = (next = {}) => {
    const p = new URLSearchParams();
    const merged = {
      channel: next.channel !== undefined ? next.channel : channel,
      surface: next.surface !== undefined ? next.surface : surface,
      agent: next.agent !== undefined ? next.agent : agent,
      identified: next.identified !== undefined ? next.identified : identified,
      page: next.page !== undefined ? next.page : page,
    };
    if (merged.channel) p.set('channel', merged.channel);
    if (merged.surface) p.set('surface', merged.surface);
    if (merged.agent) p.set('agent', merged.agent);
    if (merged.identified) p.set('identified', merged.identified);
    if (merged.page) p.set('page', String(merged.page));
    const qs = p.toString();
    // location.pathname, NOT a literal. This page moved to
    // /admin/analytics/visits when the overview took /admin/analytics, and
    // these two calls were left behind: every filter change and the clear
    // button navigated away to the overview, which looks like the filter
    // wiping itself. Reading the current path cannot go stale that way again.
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`);
  };

  const filtered = !!(channel || surface || identified || agent);

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Analytics' },
          ]} />
        </nav>
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-3">Visits</h1>
        <p className="text-sm text-gray-500 max-w-3xl">
          One row per visit, newest first. Open a visit to see what happened in
          it, in order. A chart says page views went up; this says who arrived,
          where from, and what they did.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={channel}
          onChange={(e) => go({ channel: e.target.value, page: 0 })}
          className="border-gray-300 rounded-md shadow-sm text-sm"
        >
          <option value="">Any channel</option>
          {Object.keys(CHANNEL_STYLES).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={agent}
          onChange={(e) => go({ agent: e.target.value, page: 0 })}
          className="border-gray-300 rounded-md shadow-sm text-sm"
        >
          {/* Defaults to everything, including crawlers, while the classifier
              is new: the first question is what it is actually catching, and
              a default that hides its work makes that unanswerable. */}
          <option value="">People and bots</option>
          <option value="people">People only</option>
          <option value="any_bot">Bots only</option>
          <option value="ai_crawler">AI crawlers</option>
          <option value="bot">Ordinary crawlers</option>
        </select>
        <select
          value={surface}
          onChange={(e) => go({ surface: e.target.value, page: 0 })}
          className="border-gray-300 rounded-md shadow-sm text-sm"
        >
          <option value="">Any surface</option>
          <option value="app">app</option>
          <option value="web">web</option>
          {/* Visits we could not attribute to either. Without this the two
              above can read empty with nothing to say where they went. */}
          <option value="unknown">unattributed</option>
        </select>
        <select
          value={identified}
          onChange={(e) => go({ identified: e.target.value, page: 0 })}
          className="border-gray-300 rounded-md shadow-sm text-sm"
        >
          <option value="">Signed in or not</option>
          <option value="true">Signed in</option>
          <option value="false">Anonymous</option>
        </select>
        {filtered && (
          <button type="button" onClick={() => navigate(location.pathname)} className="text-sm text-indigo-600 hover:underline">
            Clear filters
          </button>
        )}
        <span className="text-sm text-gray-500 ml-auto">{total} visits</span>
      </div>

      {error && <div className="text-sm text-red-700 mb-4">{error}</div>}
      {!error && visits === null && <div className="text-sm text-gray-500">Loading...</div>}

      {!error && visits && visits.length === 0 && (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-500 italic">
          No visits recorded yet.
        </div>
      )}

      {!error && visits && visits.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Who</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">Device</th>
                <th className="px-4 py-3">Landed on</th>
                <th className="px-4 py-3">Did</th>
                <th className="px-4 py-3">Shape</th>
                <th className="px-4 py-3">For</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {visits.map((v) => (
                <React.Fragment key={v.id}>
                  <tr
                    onClick={() => setOpenId(openId === v.id ? null : v.id)}
                    className="cursor-pointer hover:bg-gray-50 text-sm"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {formatDistanceToNow(new Date(v.startedAt), { addSuffix: true })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {v.username
                        ? <span className="text-gray-900">{v.username}</span>
                        : <span className="text-gray-400 italic">anonymous</span>}
                      {/* The visitor, not the visit: everything this browser
                          has ever done. stopPropagation so it does not also
                          toggle the row open underneath. */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/analytics/visitors/${v.visitorId}`); }}
                        className="ml-2 text-xs text-indigo-600 hover:underline"
                      >
                        history
                      </button>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Chip className={CHANNEL_STYLES[v.channel]}>{v.channel || 'unknown'}</Chip>
                      {v.utmCampaign && <span className="ml-2 text-xs text-gray-500">{v.utmCampaign}</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {v.deviceType || '?'} {v.os}{v.osVersion ? ` ${v.osVersion}` : ''}
                      <span className="ml-2 text-xs text-gray-400">{v.surface}</span>
                      {/* Named rather than merged into one "bot" label: an
                          ordinary crawler is noise to be kept out of counts,
                          an AI crawler is the evidence the AEO report is
                          built on. Same row, two different jobs. */}
                      {v.agentKind && (
                        <span
                          className={`ml-2 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${
                            v.agentKind === 'ai_crawler'
                              ? 'bg-violet-100 text-violet-700'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {v.agentKind === 'ai_crawler' ? 'AI crawler' : 'bot'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{v.landingPath || v.referrer || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {v.eventCount + v.actionCount === 0
                        ? <span className="text-amber-700">nothing</span>
                        : `${v.eventCount} ${v.eventCount === 1 ? 'view' : 'views'}, ${v.actionCount} ${v.actionCount === 1 ? 'action' : 'actions'}`}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><ActionStrip items={v.strip} /></td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">{duration(v.durationSec)}</td>
                  </tr>
                  {openId === v.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={8} className="p-0">
                        <Timeline visitId={v.id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {total > LIMIT && (
            <Pagination total={total} limit={LIMIT} page={page} navigateToPage={(p) => go({ page: Number(p) })} />
          )}
        </div>
      )}
    </Page>
  );
}
